import { Link } from "react-router-dom";
import { ArrowRight, Lightbulb, Wrench, PackageCheck, IndianRupee } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal, PageHero } from "@/components/Reveal";
import { IMG } from "@/lib/content";
import { useLang } from "@/context/LanguageContext";
import { AiotFeatureCard } from "@/components/aiot/AiotFeatureCard";

const flow = [
  { icon: Lightbulb, t: ["Ideate", "विचार करें"], d: ["Students identify real problems — at home, in hotels, on farms — during lab sessions.", "लैब सत्रों में छात्र असली समस्याएँ पहचानते हैं — घर, होटल, खेतों में।"] },
  { icon: Wrench, t: ["Build", "बनाएँ"], d: ["Teams prototype with RoboSpark kits, guided by mentors and engineers.", "टीमें मेंटर्स और इंजीनियरों के मार्गदर्शन में RoboSpark किट से प्रोटोटाइप बनाती हैं।"] },
  { icon: PackageCheck, t: ["Productise", "उत्पाद बनाएँ"], d: ["Best prototypes are refined, branded 'RoboSpark' and listed on Roboshop.", "सर्वश्रेष्ठ प्रोटोटाइप को निखारकर 'RoboSpark' ब्रांड के साथ Roboshop पर रखा जाता है।"] },
  { icon: IndianRupee, t: ["Earn", "कमाएँ"], d: ["Students receive a share of revenue from every unit sold, plus certificates.", "हर बिकी यूनिट की कमाई में छात्रों को हिस्सा और प्रमाणपत्र मिलते हैं।"] },
];

const earnings = [
  { v: "10–15%", l: ["Revenue share on co-created products", "सह-निर्मित उत्पादों पर राजस्व हिस्सा"] },
  { v: "₹5,000–₹50,000", l: ["Typical monthly student earnings", "छात्रों की सामान्य मासिक कमाई"] },
  { v: "300+", l: ["Student-made products sold", "छात्रों के बनाए उत्पाद बिके"] },
];

export default function LearnAndEarn() {
  const { L } = useLang();
  return (
    <div data-testid="learn-earn-page">
      <PageHero chip={L("Learn & Earn", "सीखें और कमाएँ")} title={L("Students who build, earn.", "जो छात्र बनाते हैं, वे कमाते हैं।")} subtitle={L("Our signature model turns classroom projects into real, sellable products — and rewards the students who created them.", "हमारा विशेष मॉडल कक्षा के प्रोजेक्ट्स को असली, बिकने योग्य उत्पादों में बदलता है — और उन्हें बनाने वाले छात्रों को पुरस्कृत करता है।")} image={IMG.circuit} testid="learn-earn-hero" />
      <AiotFeatureCard />
      <section className="bg-[#F0F4F8] text-[#0A192F] py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-slate-900/[0.07] rounded-2xl overflow-hidden border border-slate-200">
            {flow.map((s, i) => (
              <Reveal key={s.t[0]} delay={0.1 * i} className="bg-[#F0F4F8] p-8">
                <div className="font-mono text-[#FF2E63] text-sm">{L("STEP", "चरण")} 0{i + 1}</div>
                <s.icon className="w-8 h-8 text-[#007A8A] mt-4" />
                <h3 className="text-2xl font-bold mt-3">{L(...s.t)}</h3>
                <p className="text-slate-600 text-sm mt-2">{L(...s.d)}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-[#F0F4F8] py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-5">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#0A192F]">{L("Real products. Real buyers.", "असली उत्पाद। असली ख़रीदार।")}</h2>
            <p className="text-slate-600 mt-4">{L("Household AI IoT kits, humanoid robots and hotel service robots — all with student contributions — are sold to homes, hotels and institutions.", "घरेलू AI IoT किट, ह्यूमनॉइड रोबोट और होटल सर्विस रोबोट — सभी में छात्रों का योगदान — घरों, होटलों और संस्थानों को बेचे जाते हैं।")}</p>
            <div className="mt-8 flex gap-3 flex-wrap">
              <Link to="/enroll?program=Robotics"><Button className="rs-cta rounded-full px-6" data-testid="le-enroll-btn">{L("Join Premium Plan", "प्रीमियम प्लान लें")} <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
              <Link to="/roboshop"><Button variant="outline" className="rounded-full px-6" data-testid="le-shop-btn">{L("See products", "उत्पाद देखें")}</Button></Link>
              <Link to="/leaderboard"><Button variant="outline" className="rounded-full px-6" data-testid="le-leaderboard-btn">{L("View leaderboard", "लीडरबोर्ड देखें")}</Button></Link>
            </div>
          </Reveal>
          <div className="lg:col-span-7 grid sm:grid-cols-3 gap-4">
            {earnings.map((e, i) => (
              <Reveal key={e.v} delay={0.1 * i}>
                <div className="p-6 rounded-2xl bg-[#E8F0FE] border border-slate-200 h-full" data-testid={`earning-${i}`}>
                  <div className="text-2xl font-extrabold text-[#FF2E63] font-['Outfit']">{e.v}</div>
                  <div className="text-sm text-slate-600 mt-2">{L(...e.l)}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
